// Canvas-video compositing helpers: a pool of <video> elements drawn onto the
// reel canvas and kept loosely in sync with the engine's Web-Audio master clock.
// (The audio clock stays authoritative; videos follow by seeking when they drift.)

export class VideoPool {
  constructor() { this.els = new Map(); } // clipId -> HTMLVideoElement

  get(clip, onReady) {
    let el = this.els.get(clip.id);
    if (!el) {
      el = document.createElement("video");
      el.crossOrigin = null;
      el.muted = true;          // reel audio comes from VO/music/sfx, not the clip
      el.playsInline = true;
      el.preload = "auto";
      el.loop = false;
      el.src = clip.src;
      if (onReady) {
        el.addEventListener("loadeddata", onReady);
        el.addEventListener("seeked", onReady);
      }
      this.els.set(clip.id, el);
    }
    return el;
  }

  pauseExcept(activeIds) {
    for (const [id, el] of this.els) if (!activeIds.has(id) && !el.paused) { try { el.pause(); } catch {} }
  }
  pauseAll() { for (const el of this.els.values()) { try { el.pause(); } catch {} } }

  /** Drop video elements whose clips no longer exist. */
  prune(keepIds) {
    for (const [id, el] of this.els) {
      if (!keepIds.has(id)) {
        try { el.pause(); el.removeAttribute("src"); el.load(); } catch {}
        this.els.delete(id);
      }
    }
  }
  dispose() {
    for (const el of this.els.values()) { try { el.pause(); el.removeAttribute("src"); el.load(); } catch {} }
    this.els.clear();
  }
}

/** Seek/play a video element to local time `lt`, following the master clock. */
export function syncVideo(el, lt, playing) {
  lt = Math.max(0, lt);
  if (Number.isFinite(el.duration) && el.duration > 0) lt = Math.min(lt, el.duration - 0.05);
  if (playing) {
    if (el.paused) el.play().catch(() => {});
    if (Math.abs(el.currentTime - lt) > 0.3) { try { el.currentTime = lt; } catch {} }
  } else {
    if (!el.paused) el.pause();
    if (Math.abs(el.currentTime - lt) > 0.05) { try { el.currentTime = lt; } catch {} }
  }
}

/** object-fit: contain draw of an image/video source onto the canvas. */
export function drawContain(ctx, source, sw, sh, cw, ch, scale = 1) {
  if (!sw || !sh) return;
  const ir = sw / sh, cr = cw / ch;
  let w, h;
  if (ir > cr) { w = cw; h = cw / ir; } else { h = ch; w = ch * ir; }
  w *= scale; h *= scale;
  ctx.drawImage(source, (cw - w) / 2, (ch - h) / 2, w, h);
}

/** Probe a local image/video file for dimensions, duration, and a poster dataURL. */
export async function decodeMeta(file) {
  const isVideo = (file.type || "").startsWith("video/");
  const isImage = (file.type || "").startsWith("image/");
  if (isImage) {
    const url = URL.createObjectURL(file);
    try {
      const dims = await new Promise((res) => {
        const im = new Image();
        im.onload = () => res({ width: im.naturalWidth, height: im.naturalHeight });
        im.onerror = () => res({ width: 0, height: 0 });
        im.src = url;
      });
      return { ...dims, duration: 0, poster: null, isVideo: false, isImage: true };
    } finally { URL.revokeObjectURL(url); }
  }
  if (isVideo) {
    const url = URL.createObjectURL(file);
    const v = document.createElement("video");
    v.preload = "metadata"; v.muted = true; v.playsInline = true; v.src = url;
    const withTimeout = (p, ms, fallback) => Promise.race([p, new Promise((res) => setTimeout(() => res(fallback), ms))]);
    try {
      // never hang the add flow if a file's metadata/seek events never fire
      await withTimeout(new Promise((res, rej) => { v.onloadedmetadata = res; v.onerror = () => rej(new Error("metadata")); }), 8000, undefined);
      const width = v.videoWidth, height = v.videoHeight;
      const duration = Number.isFinite(v.duration) ? v.duration : 0;
      const poster = await withTimeout(new Promise((res) => {
        v.onseeked = () => {
          try {
            const pw = 320, ph = Math.max(1, Math.round(pw * (height / width || 0.5625)));
            const c = document.createElement("canvas"); c.width = pw; c.height = ph;
            c.getContext("2d").drawImage(v, 0, 0, pw, ph);
            res(c.toDataURL("image/jpeg", 0.6));
          } catch { res(null); }
        };
        try { v.currentTime = Math.min(0.1, (v.duration || 1) / 2); } catch { res(null); }
      }), 2500, null);
      return { width, height, duration, poster, isVideo: true, isImage: false };
    } finally { try { v.removeAttribute("src"); v.load(); } catch {} URL.revokeObjectURL(url); }
  }
  return { width: 0, height: 0, duration: 0, poster: null, isVideo: false, isImage: false };
}
